import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
  FormikWrapper,
  Button,
  FormTextField,
  FormCheckbox,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_SUCCESS,
  setOperationStatus,
} from '@uncinc/react-drupal-core';
import { useTranslation } from 'react-i18next';

import {
  getNewsletterContactPropertyOptions,
  createOrUpdateNewsletterContactSubscription,
  getNewsletterContact,
} from '../../../store/actions/UI';
import { prepareSpotlerListData } from '../../../helpers';

import { SPOTLER_PERMISSIONS_BITS_WHITELIST, SPOTLER_PERMISSIONS_HIDDEN_DEFAULT_BITS } from '../../../config/constants';

import './style.scss';

const SpotlerSubscriptionForm = ({ onSuccess = () => { }, hideEmail = true, subscriptionSource }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [subscriptionErrorReason, setSubscriptionErrorReason] = useState(null);
  const urlParams = new URLSearchParams(window.location.search);
  const userParamValues = Object.fromEntries(urlParams);
  const userParamEmail = userParamValues.email;
  const loggedIn = useSelector((state) => state.getIn(['login', 'loggedIn'], null));

  // User data.
  const uid = useSelector((state) => state.getIn(['login', 'uid'], 0));
  const userMail = useSelector((state) => state.getIn(['user', uid, 'mail', 0, 'value'], null));

  // Profile data.
  const profileFirstName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_first_name', 0, 'value'], null));
  const profileInfixName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_infix_name', 0, 'value'], null));
  const profileLastName = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_last_name', 0, 'value'], null));
  const profileOrganisation = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_organisation_name', 0, 'value'], null));
  const profileWorkArea = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_work_area', 0, 'value'], null));
  const profileJobTitle = useSelector((state) => state.getIn(['user', uid, 'profile', 'field_job_title', 0, 'value'], null));

  // Spotler options.
  const contactPermissionOptions = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'permissions'], null)));
  const workFieldList = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list1'], null)));
  const jobTitleList = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list2'], null)));

  // Spotler subscription data.
  const spotlerUserContact = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact'], undefined)));
  const spotlerUserContactPermissions = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact', 'properties', 'permissions'], [])));

  // Action data.
  const subscriptionSaveStatus = useSelector((state) => state.getIn(['ui', 'newsletter', 'operations', 'update', 'status']));
  const subscriptionSaveStatusReason = useSelector((state) => state.getIn(['ui', 'newsletter', 'operations', 'update', 'reason']));

  // Set up initialValues.
  const initialValues = {};
  if (userParamEmail) {
    initialValues.email = userParamEmail;
  }
  // If we have a Spotler account, set form checkboxes active via initalValues.
  spotlerUserContactPermissions.map((spotlerUserContactPermission) => {
    initialValues[`bit-${spotlerUserContactPermission.bit}`] = spotlerUserContactPermission.enabled;
  });

  // On form submision.
  const onSubmit = (data) => {
    setSubscriptionErrorReason(null);
    // Collect the selected permissions.
    const newContactPermissions = contactPermissionOptions.map((contactPropertyPermissionOption) => (
      {
        ...contactPropertyPermissionOption,
        enabled: data[`bit-${contactPropertyPermissionOption.bit}`] || SPOTLER_PERMISSIONS_HIDDEN_DEFAULT_BITS.includes(contactPropertyPermissionOption.bit),
      }
    ));

    // If we are logged-in, add extra profile data to the Spotler contact.
    let extraProfileData = {};
    if (uid !== 0) {
      // Prepare list data.
      const spotlerListData = prepareSpotlerListData(
        { fields: workFieldList, selected: profileWorkArea },
        { fields: jobTitleList, selected: profileJobTitle },
      );
      extraProfileData = {
        firstName: profileFirstName,
        lastName: profileInfixName ? `${profileInfixName} ${profileLastName}` : profileLastName,
        organisation: profileOrganisation,
        ...spotlerListData,
      };
    }
    // Create or update our Spotler newsletter subscription.
    dispatch(createOrUpdateNewsletterContactSubscription({
      email: userMail || data.email,
      permissions: newContactPermissions,
      ...extraProfileData,
    }));
  };

  // React to the submission save.
  useEffect(() => {
    if (subscriptionSaveStatus === UI_OPERATION_STATUS_SUCCESS) {
      if (loggedIn) {
        toast.success(t('form.newsletter.update_success'));
      }

      // Handle onSuccess.
      if (onSuccess) {
        onSuccess(!spotlerUserContact);
      }

      // Push GTM event.
      if (typeof window !== 'undefined') {
        let loginStatus = 'create_non_logged_in';
        if (spotlerUserContact) {
          loginStatus = 'updated';
        } else if (loggedIn) {
          loginStatus = 'create_logged_in';
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'email_subscription_success',
          login_status: loginStatus,
          subscription_source: subscriptionSource,
        });
      }

      // Refetch the newsletter contact.
      dispatch(getNewsletterContact(userMail));
    } else if (subscriptionSaveStatus === UI_OPERATION_STATUS_ERROR) {
      setSubscriptionErrorReason(subscriptionSaveStatusReason);
    }
    dispatch(setOperationStatus('newsletter', 'update', null, null));
  }, [subscriptionSaveStatus]);

  // Try to fetch our Spotler account if we are logged in.
  useEffect(() => {
    if (userMail && !spotlerUserContact) {
      dispatch(getNewsletterContact(userMail));
    }
  }, [userMail]);

  // Fetch the available Contact property options for the form.
  useEffect(() => {
    if (contactPermissionOptions === null) {
      dispatch(getNewsletterContactPropertyOptions());
    }
  }, []);

  return (
    <div className="SpotlerSubscriptionForm">
      <FormikWrapper
        onSubmit={onSubmit}
        initialValues={initialValues}
        key={`spotler-subscription-form-${spotlerUserContactPermissions.length}`}
      >
        {((loggedIn === false || spotlerUserContact !== undefined) && contactPermissionOptions !== null) && (
          <>
            {/* If we are logged-in we don't want to display the email field. */}
            {!hideEmail && (
              <div className="PageLogin__email">
                <FormTextField
                  title={t('login.email.label')}
                  type="email"
                  fieldName="email"
                  required
                />
              </div>
            )}
            {subscriptionErrorReason && (
              <div className="Form__error">
                <WysiwygField
                  property="schema:text"
                  html={t(`form.newsletter.errors.${subscriptionErrorReason}`)}
                />
              </div>
            )}
            {/* Display in the available permissions from Spotler. */}
            {contactPermissionOptions && (
              <div>
                <div className="WebformCheckboxes">
                  {contactPermissionOptions.map((contactPropertyPermissionOption) => {
                    return SPOTLER_PERMISSIONS_BITS_WHITELIST.includes(contactPropertyPermissionOption.bit) && (
                      <div key={`checkbox-bit-${contactPropertyPermissionOption.bit}`}>
                        <FormCheckbox
                          title={contactPropertyPermissionOption.description}
                          fieldName={`bit-${contactPropertyPermissionOption.bit}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* If we already have a Spotler account then the user doesn't have to agree to the terms again. */}
            {!loggedIn ? (
              <div>
                <div className="WebformCheckboxes privacy">
                  <WysiwygField
                    property="schema:text"
                    html={t('spotler.privacy_checkbox')}
                  />
                  <FormCheckbox
                    title={t('form.register.privacy_label')}
                    fieldName="privacy"
                    required
                  />
                </div>
              </div>
            ) : (
              <div className="WebformCheckboxes privacy">
                <WysiwygField
                  property="schema:text"
                  html={t('spotler.privacy')}
                />
              </div>
            )}
            <div className="Form__actions">
              <Button
                type="submit"
                className="Button Button--primary"
              >
                {spotlerUserContact ? (
                  <span>{t('general.save')}</span>
                ) : (
                  <>
                    {t('spotler.subscribe')}
                    <i className="icon icon-arrow-right" />
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </FormikWrapper>
    </div>
  );
};

export default SpotlerSubscriptionForm;
